/**
 * Client-Helper für Bulk-ZIP-Download aus der Library-Selektion.
 *
 * Zwei-Schritt: prepareBulkDownload() POSTet die Asset-IDs (CSRF via Origin-Header,
 * der Browser setzt ihn auf same-origin-Mutations) und bekommt einen single-use
 * Token zurück; triggerBrowserDownload(bulkDownloadHref(token)) startet danach den
 * nativen Browser-Download (GET, streamt auf Platte — kein Blob im RAM).
 *
 * prepareBulkDownload wirft NICHT, sondern liefert ein discriminated-union-Result,
 * damit der Caller (BulkActionBar) sauber auf Toast-Texte mappen kann.
 */
import { api, ApiError } from './api';

export interface BulkDownloadPrepareResult {
  token: string;
  /** Anzahl tatsächlich gefundener (nicht-gelöschter) Assets im ZIP. */
  count: number;
  totalBytes: number;
  /** Angefragte IDs, die fehlen/gelöscht sind (dedupliziert gezählt). */
  skipped: number;
}

export type BulkDownloadError =
  | {
      kind: 'too_large';
      limitAssets: number;
      limitBytes: number;
      actualAssets: number;
      actualBytes: number;
    }
  | { kind: 'empty' }
  /** Concurrent-Stream-Cap erreicht (prepare-Preflight) → "kurz warten". */
  | { kind: 'busy' }
  | { kind: 'other'; message: string };

export type PrepareOutcome =
  | { ok: true; data: BulkDownloadPrepareResult }
  | { ok: false; error: BulkDownloadError };

export async function prepareBulkDownload(ids: string[]): Promise<PrepareOutcome> {
  try {
    const data = await api<BulkDownloadPrepareResult>('/api/assets/bulk-download', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
    return { ok: true, data };
  } catch (err) {
    if (err instanceof ApiError) {
      const body = err.body;
      if (err.status === 413 && body?.error === 'selection_too_large') {
        return {
          ok: false,
          error: {
            kind: 'too_large',
            limitAssets: Number(body.limitAssets),
            limitBytes: Number(body.limitBytes),
            actualAssets: Number(body.actualAssets),
            actualBytes: Number(body.actualBytes),
          },
        };
      }
      if (err.status === 400 && body?.error === 'no_downloadable_assets') {
        return { ok: false, error: { kind: 'empty' } };
      }
      // Concurrent-stream-Cap erreicht (prepare-Preflight). Client-sichtbar im
      // POST, weil der GET-Stream per Browser-Navigation läuft (503 dort würde
      // die SPA wegnavigieren). Eigenes 'busy'-kind → der Caller zeigt einen
      // Wartehinweis (NICHT das generische "Download fehlgeschlagen").
      if (err.status === 503 && body?.error === 'too_many_downloads') {
        return { ok: false, error: { kind: 'busy' } };
      }
      return { ok: false, error: { kind: 'other', message: err.message } };
    }
    return { ok: false, error: { kind: 'other', message: 'Netzwerkfehler' } };
  }
}

/** GET-URL für den ZIP-Stream eines ausgestellten Tokens. */
export function bulkDownloadHref(token: string): string {
  return `/api/assets/bulk-download/${encodeURIComponent(token)}`;
}

/** Direkter Single-Asset-Download (Original) — für count===1 ohne ZIP-Overhead. */
export function singleDownloadHref(id: string): string {
  return `/api/assets/${encodeURIComponent(id)}/download`;
}

/**
 * Triggert einen nativen Browser-Download über einen synthetischen <a>-Click.
 * Kein `download`-Attribut — der Server-Header (Content-Disposition) steuert den
 * Dateinamen. Gleiches Muster wie der bisherige Einzel-Download in BulkActionBar.
 */
export function triggerBrowserDownload(href: string): void {
  const a = document.createElement('a');
  a.href = href;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
